import { createSelector } from '@reduxjs/toolkit';

import { createWeakMapSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { domainLogosSelectors } from './domainLogosSlice';
import { DOMAIN_LOGOS_REDUCER_KEY } from './domainLogosTypes';

export { domainLogosSelectors as stdSliceSelectors };
export const getDomainLogosState = (state: RootState) => state[DOMAIN_LOGOS_REDUCER_KEY];

export const getIsLoading = (state: RootState) => getDomainLogosState(state).isLoading;
export const getError = (state: RootState) => getDomainLogosState(state).error;

export const selectLogoByUrl = domainLogosSelectors.selectById;

/** True when a lookup already resolved this domain without a logo. */
export const selectIsResolvedLogoMiss = (state: RootState, domain?: string | null): boolean =>
  Boolean(domain && getDomainLogosState(state).resolvedMisses[domain]);

export const getDomainForId = createWeakMapSelector(
  [domainLogosSelectors.selectAll, (_state: RootState, id?: string | null) => id],
  // because selectAll returns an array, the entries are not stable,
  //  so we need to find the domain for a stable lookup
  (domainLogosArray, id) => domainLogosArray.find((entry) => entry.id === id)?.domain,
);

export const selectLogoById = (state: RootState, id?: string | null) => {
  const domain = getDomainForId(state, id);
  // stable lookup with domain
  return domain ? selectLogoByUrl(state, domain) : null;
};

/**
 * Generic selector to enrich any list of items with a domain logo.
 *
 * @param getLogoKey - Function to extract the logoKey from each item
 */

const emptyObj = {};

export function makeSelectItemsWithLogos<T extends object>(getLogoKey: (item: T) => string) {
  return createSelector(
    [
      (state: RootState) => domainLogosSelectors.selectEntities(state) ?? emptyObj,
      (_state: RootState, items: T[]) => items,
    ],
    (logoEntities, items) =>
      items?.map((item) => {
        const logoKey = getLogoKey(item);
        const logo = logoKey ? logoEntities[logoKey] : undefined;
        return {
          ...item,
          logoKey: logo?.logoUrl ?? null,
        };
      }),
  );
}
